import './MonitorInfo.scss';
import {
  Button,
  Grid,
  GridItem,
  Heading,
  Icons,
  PendingContent,
  Space,
  Spinner,
} from 'plume-ui';
import { HeadingStyles } from 'plume-ui/dist/components/Heading/Heading';
import React, { memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { Routes } from '../../../config';
import { DependencyContainer } from '../../../DependencyContainer';
import { useFeaturePermission } from '../../../hooks/useFeaturePermission';
import { useFetchData } from '../../../hooks/useFetchData';
import { MixPanelEvents } from '../../../mixPanelEvents';
import { activePartnerAtom } from '../../../state';
import { Permission } from '../../../userPermissionConfig';
import FormattedMessage from '../../../utils/components/FormattedMessage';
import {
  AdjustableComponent,
  useClasses,
} from '../../../utils/hooks/useClasses';
import { useRedirectToRoute } from '../../../utils/hooks/useRedirectToRoute';
import useWithConfirmation from '../../../utils/hooks/useWithConfirmation';
import { useTrackEvent } from '../../trackingAnalytics/hooks/useTrackEvent';
import { AvailableScreens } from '../../trackingAnalytics/types';
import MonitorInfoBasic from '../components/MonitorInfo/MonitorInfoBasic';
import MonitorInfoWithOutreach from '../components/MonitorInfo/MonitorInfoWithOutreach';
import TriggeredOutreachModal from '../components/MonitorInfo/TriggeredOutreachModal';
import { Monitor, MonitorsTab } from '../types';

type MonitorInfoProps = {
  id?: number | 0;
};

export type MonitorInfoStyles = {
  root: string;
  controlPanel: string;
  header: string;
  infoCard: string;
};

const MonitorInfo: AdjustableComponent<MonitorInfoProps, MonitorInfoStyles> = ({
  classes,
}) => {
  const { t } = useTranslation();
  const redirectToRoute = useRedirectToRoute();
  const { monitorId } = useParams<{ monitorId: string }>();
  const { monitorsService } = new DependencyContainer();
  const activePartner = useRecoilValue(activePartnerAtom);
  const withConfirmation = useWithConfirmation();
  const { hasPermission } = useFeaturePermission();
  const [loading, monitor, monitorLoadError] = useFetchData<Monitor, any>(
    async () => {
      if (!activePartner?.id || !monitorId) {
        return;
      }
      return await monitorsService.getMonitorId(activePartner.id, monitorId);
    },
    undefined,
    [activePartner, monitorId],
  );
  const trackEvent = useTrackEvent();

  useEffect(() => {
    trackEvent({
      eventName: MixPanelEvents.SCREEN,
      additionalContent: {
        SCREEN: AvailableScreens.MonitorDetail,
      },
    });
  }, []);

  const styles: MonitorInfoStyles = useClasses(
    {
      root: 'MonitorInfo',
      controlPanel: 'MonitorInfo__control-panel',
      header: 'MonitorInfo__header',
      infoCard: 'MonitorInfo__info-card',
    },
    classes,
  );

  const deleteMonitor = () => {
    withConfirmation({
      title: t('monitors.deleteConfirmationTitle'),
      body: t('monitors.deleteConfirmationMessage', {
        monitorName: monitor?.name,
      }),
      confirmButtonLabel: t('delete'),
      onConfirm: async () => {
        if (!activePartner?.id) return;
        await monitorsService.deleteMonitor(activePartner.id, monitorId);
        redirectToRoute(Routes.MonitorsTab, {
          partnerId: activePartner.id,
          tab: MonitorsTab.User,
        });
      },
    });
  };

  if (!activePartner?.id) return null;

  return (
    <div className={styles.root}>
      <PendingContent
        loading={loading}
        loader={Spinner}
        isError={monitorLoadError}
        transparent
      >
        {monitor && (
          <>
            <div className={styles.controlPanel}>
              <Button
                onClick={() =>
                  redirectToRoute(Routes.MonitorsTab, {
                    partnerId: activePartner.id,
                    tab: MonitorsTab.User,
                  })
                }
                styleVariant="tertiary-grey"
              >
                <Icons.ArrowLeftIcon width={16} />
                <FormattedMessage id="actions.back" />
              </Button>
              <Button
                styleVariant="secondary"
                onClick={() => deleteMonitor()}
                disabled={!hasPermission(Permission.DeleteMonitor)}
              >
                <FormattedMessage id="actions.delete" />
              </Button>
            </div>
            <Grid>
              <GridItem colSpan="12" tabletColSpan="12">
                <Space size="l" />
                <Heading
                  classes={(current: HeadingStyles) => ({
                    root: `${current.root} AppLayout__header ${styles.header}`,
                  })}
                  size="xxxxl"
                  medium
                >
                  {monitor?.name}
                </Heading>
                <Space size="s" />
              </GridItem>
            </Grid>
            <MonitorInfoBasic data={monitor} />
            <Space size="m" />
            <MonitorInfoWithOutreach
              outreachConfig={monitor.outreach}
              monitorId={monitorId}
            />
            <Space size="xxxl" />
            <TriggeredOutreachModal
              partnerId={activePartner.id}
              monitorId={monitorId}
            />
          </>
        )}
      </PendingContent>
    </div>
  );
};

export default memo(MonitorInfo);
